import {
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  type ForwardedRef,
  type ReactElement,
  type SyntheticEvent,
} from 'react';

import { FormContext, type FormContextValue } from './context/FormContext.js';
import type {
  FormApi,
  FormInvalidContext,
  FormSubmitContext,
  FormValidator,
} from './form.types.js';
import {
  createFormRuntime,
  type FormRuntime,
  type FormRuntimeConfig,
} from './runtime/createFormRuntime.js';
import { registerFormById } from './runtime/formRegistry.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useSafeLayoutEffect } from '../../core/hooks/useSafeLayoutEffect.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import type { ValidationState } from '../validation/types.js';

/**
 * Props for `Form`.
 * @internal
 */
export interface FormProps<TValues = Record<string, unknown>>
  extends WithChildren,
    StylingProps,
    DataTestId,
    AriaLabelingProps,
    BehaviorTrackingProps {
  /**
   * Native form id. Required to associate `FormMessages` or a control from
   * outside the React subtree via `form="…"`; optional otherwise.
   */
  id?: string;
  /** Form-level validators. Receive the full snapshot and may be async. */
  validators?: FormValidator<TValues>[];
  /** Called after successful validation on a submit trigger. */
  onSubmit?: (context: FormSubmitContext<TValues>) => void;
  /** Called after failed validation on a submit trigger. */
  onInvalid?: (context: FormInvalidContext<TValues>) => void;
  /**
   * Called when the form's aggregate validation state transitions — validity
   * flips, or the set of errors changes. Not called on identical recomputations.
   */
  onValidityChange?: (state: ValidationState) => void;
  /**
   * Pre-built runtime created by `useForm` or `createForm`. When provided,
   * the form uses this runtime instead of creating its own, so callers outside
   * the subtree can drive `submit()`, `setError()`, etc. via the same handle.
   * @internal
   */
  form?: FormApi<TValues>;
}

const EMPTY_VALIDATORS: FormValidator<never>[] = [];

/**
 * Narrows a `FormApi` (or unknown value) to a full `FormRuntime` by checking
 * for the runtime-only methods (`register`, `subscribe`, `getFormMessages`,
 * `getFieldMessages`). `useForm` / `createForm` return the underlying runtime
 * widened to `FormApi`, so this succeeds for legitimate handles.
 * @internal
 */
export function isFormRuntime<TValues = Record<string, unknown>>(
  value: unknown,
): value is FormRuntime<TValues> {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.register === 'function' &&
    typeof candidate.subscribe === 'function' &&
    typeof candidate.getFormMessages === 'function' &&
    typeof candidate.getFieldMessages === 'function'
  );
}

const FormRoot = /* @__PURE__ */ forwardRef(function Form<
  TValues = Record<string, unknown>,
>(props: FormProps<TValues>, forwardedRef: ForwardedRef<HTMLFormElement>) {
  const {
    children,
    id,
    form,
    validators = EMPTY_VALIDATORS as FormValidator<TValues>[],
    onSubmit,
    onInvalid,
    onValidityChange,
    className,
    style,
    'data-testid': dataTestId,
    ...remainingProps
  } = props;

  const formMessagesId = useId();

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);

  // Keep the runtime's view of the latest props current without mutating it
  // during render: the runtime reads this ref lazily on submit/validate.
  const configRef = useRef<FormRuntimeConfig<TValues>>({
    validators: EMPTY_VALIDATORS as FormValidator<TValues>[],
  });
  configRef.current = {
    formId: id,
    validators,
    onSubmit,
    onInvalid,
    onValidityChange,
  };

  const externalRuntime = isFormRuntime<TValues>(form) ? form : undefined;

  // Internal runtime is only created when no external `form` is provided.
  // If the consumer toggles between defined/undefined across renders the active
  // runtime identity swaps — this is a deliberate non-goal for the `_`-prefixed
  // unstable API; avoid doing it.
  const internalRuntimeRef = useRef<FormRuntime<TValues> | undefined>(
    undefined,
  );
  let runtime: FormRuntime<TValues>;
  if (externalRuntime !== undefined) {
    runtime = externalRuntime;
  } else {
    internalRuntimeRef.current ??= createFormRuntime<TValues>(
      () => configRef.current,
    );
    runtime = internalRuntimeRef.current;
  }

  // Wire the Form's config into an externally-provided runtime. useSafeLayoutEffect
  // fires before child useEffect registrations, so config is live before any
  // submit/validate can read it. On unmount the binding is cleared.
  useSafeLayoutEffect(() => {
    const bindConfig = externalRuntime?._bindConfig;
    if (!bindConfig) {
      return;
    }
    bindConfig(() => configRef.current);
    return () => bindConfig(undefined);
  }, [externalRuntime]);

  // Expose the runtime under its id so out-of-subtree controls can associate.
  useEffect(() => {
    if (id === undefined) {
      return;
    }
    return registerFormById(id, runtime as FormRuntime);
  }, [id, runtime]);

  const contextValue = useMemo<FormContextValue>(
    () => ({ runtime: runtime as FormRuntime, formId: id, formMessagesId }),
    [runtime, id, formMessagesId],
  );

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
    // We own validation and submission; keep the browser out of both.
    event.preventDefault();
    void runtime.submit();
  };

  return (
    <FormContext.Provider value={contextValue}>
      <form
        ref={forwardedRef}
        id={id}
        noValidate
        onSubmit={handleSubmit}
        className={className}
        style={style}
        data-testid={dataTestId}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
      >
        {children}
      </form>
    </FormContext.Provider>
  );
});

(FormRoot as typeof FormRoot & { displayName: string }).displayName = 'Form';

/**
 * Aggregates the validation state of the `FormField`s it wraps into a single
 * submit / validity flow, without owning any control values.
 * @internal
 */
export const Form = FormRoot as <TValues = Record<string, unknown>>(
  props: FormProps<TValues> & { ref?: ForwardedRef<HTMLFormElement> },
) => ReactElement;
